import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import './Depth.css';

/* Site-wide 3D parallax primitives.
 *
 * DepthSection — wraps a whole section: it enters tilted back in perspective
 * (rotateX + depth-rise), settles perfectly flat while being read, then
 * gently recedes as it leaves. Scroll-driven, transform-only.
 *
 * ParallaxImg — an image that drifts vertically inside its (overflow-hidden)
 * frame while scrolling, slightly overscanned so edges never show.
 *
 * IMPORTANT: never put position:fixed / sticky elements inside DepthSection —
 * transforms break their containing block. Keep floats/nav as siblings. */

export const DepthSection = ({ children }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.16, 0.9, 1], [7, 0, 0, -3]);
  const y = useTransform(scrollYProgress, [0, 0.16, 0.9, 1], [64, 0, 0, -36]);
  const scale = useTransform(scrollYProgress, [0, 0.16, 0.9, 1], [0.968, 1, 1, 0.988]);
  const opacity = useTransform(scrollYProgress, [0, 0.13], [0.5, 1]);

  if (reduce) return <>{children}</>;

  return (
    <div ref={ref} style={{ perspective: '1400px' }}>
      <motion.div
        style={{
          rotateX,
          y,
          scale,
          opacity,
          transformOrigin: '50% 18%',
          willChange: 'transform'
        }}
      >
        {children}
      </motion.div>
    </div>
  );
};

export const ParallaxImg = ({ strength = 16, style, className = '', ...imgProps }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [-strength, strength]);

  return (
    <motion.img
      ref={ref}
      className={`parallax-img ${className}`.trim()}
      {...imgProps}
      style={
        reduce
          ? style
          : { ...style, y, scale: 1 + (strength * 2) / 240, willChange: 'transform' }
      }
    />
  );
};
